import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import TopBar from "./components/TopBar";
import HomeScreen from "./screens/HomeScreen";
import CatalogScreen from "./screens/CatalogScreen";
import SearchScreen from "./screens/SearchScreen";
import AuthScreen from "./screens/AuthScreen.jsx";
import { useAccount } from "./api/auth.js";

// Signed-in users pull their synced progress/My List once per app load and
// merge into localStorage (newest-wins) so every device shows the same state.
function AccountSync() {
  const account = useAccount();
  useEffect(() => {
    if (!account?.token) return;
    let cancelled = false;
    (async () => {
      try {
        const { authFetch } = await import("./api/auth.js");
        const res = await authFetch("/api/auth/data", {}, account.token);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        const progress = await import("./storage/progress.js");
        const watchlist = await import("./storage/watchlist.js");
        progress.mergeFromServer(data.progress || []);
        watchlist.mergeFromServer(data.watchlist || []);
      } catch { /* offline / server down — localStorage still works */ }
    })();
    return () => { cancelled = true; };
  }, [account?.token]);
  return null;
}
import LiveTVScreen from "./screens/LiveTVScreen";
import MyListScreen from "./screens/MyListScreen";
import MovieDetailScreen from "./screens/MovieDetailScreen";
import TvDetailScreen from "./screens/TvDetailScreen";
import PlayerScreen from "./screens/PlayerScreen";
import AdminScreen from "./screens/AdminScreen";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Shell({ children }) {
  // TV mode (?tv=1): the Fire TV app loads pages in its own WebView with its
  // own top bar — the site's header must not render (it bled through the
  // Live TV player as a low-opacity strip). One-time read; never changes.
  const isTv = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("tv") === "1";
  useEffect(() => {
    if (isTv) document.body.classList.add("tv");
    return () => {
      if (isTv) document.body.classList.remove("tv");
    };
  }, [isTv]);
  return (
    <>
      {!isTv && <TopBar />}
      {children}
    </>
  );
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <AccountSync />
        <Routes>
        <Route path="/" element={<Shell><HomeScreen /></Shell>} />
        <Route path="/movies" element={<Shell><CatalogScreen kind="movies" /></Shell>} />
        <Route path="/shows" element={<Shell><CatalogScreen kind="shows" /></Shell>} />
        <Route path="/my-list" element={<Shell><MyListScreen /></Shell>} />
        <Route path="/live" element={<Shell><LiveTVScreen /></Shell>} />
        <Route path="/search" element={<Shell><SearchScreen /></Shell>} />
        <Route path="/auth" element={<Shell><AuthScreen /></Shell>} />
        <Route path="/movie/:id" element={<Shell><MovieDetailScreen /></Shell>} />
        <Route path="/show/:id" element={<Shell><TvDetailScreen /></Shell>} />
        <Route path="/watch" element={<PlayerScreen />} />
        <Route path="/admin" element={<Shell><AdminScreen /></Shell>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}